import React, { useEffect, useRef, useState } from 'react';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import Loader from '@amzn/meridian/loader';
import Text from '@amzn/meridian/text';
import { createEmbeddingContext } from 'amazon-quicksight-embedding-sdk';
import { useDocumentTitle } from 'src/hooks/useDocumentTitle';
import { useQuicksightControllerGenerateTiktokVtaDashboardEmbedUrlMutation } from 'src/store/leela-api';
import { useTranslation } from 'src/arb-wrapper';

export const ReportingDashboard = () => {
  const { t } = useTranslation();
  useDocumentTitle(t('reporting_dashboard'));

  const containerRef = useRef<HTMLDivElement>(null);
  const [embedError, setEmbedError] = useState(false);
  const [isDashboardLoading, setIsDashboardLoading] = useState(true);

  const [generateEmbedUrl, { data, isLoading: isGenerateEmbedUrlLoading, error }] =
    useQuicksightControllerGenerateTiktokVtaDashboardEmbedUrlMutation();

  useEffect(() => {
    generateEmbedUrl();
  }, [generateEmbedUrl]);

  useEffect(() => {
    const embedDashboard = async () => {
      if (!data?.embedUrl || !containerRef.current) return;

      try {
        const embeddingContext = await createEmbeddingContext();

        await embeddingContext.embedDashboard({
          url: data.embedUrl,
          container: containerRef.current,
          height: '100%',
          width: '100%',
          onChange: (changeEvent) => {
            if (changeEvent.eventName === 'FRAME_LOADED') {
              setIsDashboardLoading(false);
            }
          },
        });
      } catch {
        setEmbedError(true);
        setIsDashboardLoading(false);
      }
    };

    embedDashboard();
  }, [data?.embedUrl]);

  const showLoader = isGenerateEmbedUrlLoading || isDashboardLoading;

  return (
    <Column height="100%">
      <Heading level={1}>{t('reporting_dashboard')}</Heading>
      {(error || embedError) && <Text>{t('reporting_dashboard_error')}</Text>}
      {showLoader && (
        <Row alignmentHorizontal="center">
          <Loader />
        </Row>
      )}
      <div
        ref={containerRef}
        style={{
          display: showLoader ? 'none' : 'block',
          height: '80vh',
          width: '100%',
        }}
      />
    </Column>
  );
};
